import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { StandardEvents } from '@shopify/events';

const GBB_ATC_SELECTOR = [
  '.gbbMixAddtoCartBtn',
  '.gbbMixAddtoCartBtnV2',
  '.gbbMixInlineAddToCartBtn',
  '.gbbMixInlineAddToCartBtnV2',
  '.gbbMixCascadeAddToCartBtn',
  '.gbbMixCascadeAddToCartBtnV2',
  '.gbbMixVariantModalAddBtn',
  '.gbbMixBundleAddToCartBtnWrapper',
  '[class*="gbbMixInlineAddToCartBtn"]',
  '[class*="gbbMixAddtoCartBtn"]',
  '[class*="gbbMix"][class*="AddToCart"]',
  '[class*="gbbMix"][class*="AddtoCart"]',
  '[class*="gbbExt"][class*="AddToCart"]',
  '[class*="gfb"][class*="AddToCart"]',
  '[class*="gfb"][class*="AddtoCart"]',
].join(',');

const BUNDLE_WIDGET_SELECTOR = [
  '[class*="gbbMix"]',
  '[class*="gbbExt"]',
  '[class*="gfb"]',
  '[id*="gfbBundle"]',
  '[id*="fly_bundles"]',
  '[id*="fly-bundles"]',
].join(',');

/** @type {Map<string, {id: string, options: Record<string, string>, values: string[], title: string}>} */
const remoteVariantMap = new Map();

/** @type {Map<string, Promise<{id: string, options: Record<string, string>, values: string[], title: string} | null>>} */
const pendingVariantFetches = new Map();

/** @type {Set<string>} */
const fetchedVariantIds = new Set();

let cartAddGuardInstalled = false;

/**
 * @typedef {object} VariantNoticeRefs
 * @property {HTMLElement[]} [notices] - Notice panels.
 * @property {HTMLElement} [noticeList] - Inline notice list.
 * @property {HTMLDialogElement} [modal] - Confirmation dialog.
 * @property {HTMLElement} [modalBody] - Dialog body that hosts the notice.
 * @property {HTMLButtonElement} [modalClose] - Dialog close button.
 * @property {HTMLButtonElement} [modalConfirm] - Dialog continue button.
 */

/**
 * Shows the matching variant notice and gates add to cart until it is valid.
 * @extends Component<VariantNoticeRefs>
 */
class VariantNoticeComponent extends Component {
  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {MutationObserver | null} */
  #mutationObserver = null;

  /** @type {string} */
  #pendingRequiredVariantId = '';

  /** @type {boolean} */
  #preferModal = false;

  /** @type {HTMLElement | null} */
  #modalNotice = null;

  /** @type {HTMLDialogElement | null} */
  #modal = null;

  /** @type {HTMLElement | null} */
  #modalBody = null;

  /** @type {Element | null} */
  #retryBundleButton = null;

  /** @type {boolean} */
  #userInitiatedAdd = false;

  /** @type {boolean} */
  #closingModal = false;

  /** @type {boolean} */
  #syncingRemote = false;

  connectedCallback() {
    super.connectedCallback();
    installCartAddGuard();

    const { signal } = this.#abortController;
    const section = this.closest('.shopify-section');
    section?.addEventListener(StandardEvents.productSelect, this.#onProductSelect, { signal });
    section?.addEventListener('change', this.#onSectionChange, { signal });
    section?.addEventListener('input', this.#onSectionChange, { signal });
    section?.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantityUpdate, { signal });
    document.addEventListener('click', this.#onDocumentClick, { capture: true, signal });
    document.addEventListener('change', this.#onBundleChange, { signal });

    if (this.refs.modal instanceof HTMLDialogElement) this.#modal = this.refs.modal;
    if (this.refs.modalBody instanceof HTMLElement) this.#modalBody = this.refs.modalBody;
    this.refs.modalClose?.addEventListener('click', this.#onModalCloseClick, { signal });
    this.refs.modalConfirm?.addEventListener('click', this.#onModalConfirm, { signal });
    this.#modal?.addEventListener('close', this.#onModalClose, { signal });
    this.#modal?.addEventListener('click', this.#onModalBackdropClick, { signal });
    this.#modal?.addEventListener('change', this.#onNoticeChange, { signal });

    this.addEventListener('change', this.#onNoticeChange);
    this.#observeBundleBuilder();
    patchGbbAddMethods();
    this.update();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#closeNoticeModal({ restore: true });
    if (this.#modal?.parentElement === document.body) {
      this.appendChild(this.#modal);
    }
    this.#mutationObserver?.disconnect();
    this.#abortController.abort();
  }

  /**
   * @param {Event & { promise?: Promise<unknown> }} event
   */
  #onProductSelect = (event) => {
    this.update();
    event.promise
      ?.then(() => {
        this.update();
      })
      .catch(() => {});
  };

  /**
   * @param {Event} event
   */
  #onSectionChange = (event) => {
    if (!(event.target instanceof Element)) return;
    if (event.target.closest('variant-picker')) {
      this.update();
      return;
    }
    if (event.target.closest('quantity-selector-component')) this.update();
  };

  /**
   * @param {Event} event
   */
  #onQuantityUpdate = (event) => {
    if (/** @type {{detail?: {cartLine?: number}}} */ (event).detail?.cartLine) return;
    this.update();
  };

  /**
   * @param {Event} event
   */
  #onNoticeChange = (event) => {
    if (!(event.target instanceof Element)) return;
    const notice = event.target.closest('[ref="notices[]"]');
    this.#syncOtherField(notice instanceof HTMLElement ? notice : null);
    this.#syncPurchaseControls();
  };

  /**
   * @param {MouseEvent} event
   */
  #onDocumentClick = (event) => {
    if (!(event.target instanceof Element)) return;

    const bundleButton = event.target.closest(GBB_ATC_SELECTOR);
    if (bundleButton) {
      this.#harvestBundleVariants();
      this.update();
      if (this.#isGlobal() && this.#productComponentCovers(undefined, { gbbRequest: true })) return;
      if (!this.blocksBundleAdd()) return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.#userInitiatedAdd = true;
      this.#preferModal = true;
      this.#retryBundleButton = bundleButton;
      this.reportNoticeValidity();
      return;
    }

    if (event.target.closest(BUNDLE_WIDGET_SELECTOR)) {
      window.setTimeout(() => {
        this.#harvestBundleVariants();
        this.update();
      }, 0);
    }
  };

  /**
   * @param {Event} event
   */
  #onBundleChange = (event) => {
    if (!(event.target instanceof Element)) return;
    if (!event.target.closest(BUNDLE_WIDGET_SELECTOR)) return;
    this.#harvestBundleVariants();
    this.update();
  };

  update() {
    const selected = this.#selectedOptions();
    const notices = this.#noticeElements();
    let anyActive = false;

    this.#harvestBundleVariants();
    const bundleVariantIds = this.#bundleSelectedVariantIds();
    const modalOpen = this.#isModalOpen();

    for (const notice of notices) {
      const productAssigned = notice.dataset.productAssigned !== 'false';
      const themeMatch = productAssigned && this.#noticeMatches(notice, selected);
      const bundleMatch = bundleVariantIds.some((variantId) => this.#variantRequiresNotice(variantId, notice));
      const inModal = this.#modalNotice === notice;

      if (inModal) {
        notice.hidden = false;
        this.#setFieldsEnabled(notice, true, themeMatch);
        this.#syncOtherField(notice);
        continue;
      }

      notice.hidden = !themeMatch;
      this.#setFieldsEnabled(notice, themeMatch || bundleMatch, themeMatch);
      delete notice.dataset.cartRequired;
      if (themeMatch) {
        anyActive = true;
        this.#syncOtherField(notice);
      } else if (bundleMatch) {
        this.#syncOtherField(notice);
      }
    }

    this.toggleAttribute('data-active', anyActive);
    if (this.#isGlobal()) {
      this.hidden = !modalOpen;
    } else {
      this.hidden = !anyActive && !modalOpen;
    }
    this.#syncPurchaseControls();
    this.#syncRemoteVariants();
  }

  /**
   * @returns {boolean}
   */
  #isGlobal() {
    return this.dataset.scope === 'global';
  }

  /**
   * @returns {HTMLElement[]}
   */
  #noticeElements() {
    /** @type {HTMLElement[]} */
    const notices = [];
    const roots = [this.refs.noticeList, this.#modalBody];
    for (const root of roots) {
      if (!(root instanceof HTMLElement)) continue;
      for (const notice of root.querySelectorAll(':scope > .variant-notice')) {
        if (notice instanceof HTMLElement && !notices.includes(notice)) notices.push(notice);
      }
    }
    if (this.#modalNotice instanceof HTMLElement && !notices.includes(this.#modalNotice)) {
      notices.push(this.#modalNotice);
    }
    if (notices.length) return notices;

    const refs = this.refs.notices;
    if (Array.isArray(refs)) return refs.filter((notice) => notice instanceof HTMLElement);
    if (refs instanceof HTMLElement) return [refs];
    return [];
  }

  /**
   * @returns {Record<string, string>}
   */
  #selectedOptions() {
    /** @type {Record<string, string>} */
    const selected = {};
    const section = this.closest('.shopify-section');
    const picker = section?.querySelector('variant-picker');
    if (!picker) return selected;

    const inputs = picker.querySelectorAll('input:checked, select');
    for (const input of inputs) {
      if (!(input instanceof HTMLInputElement) && !(input instanceof HTMLSelectElement)) continue;
      const optionName = this.#optionNameFrom(input);
      if (!optionName || !input.value) continue;
      selected[optionName] = input.value;
    }

    return selected;
  }

  /**
   * @param {HTMLInputElement | HTMLSelectElement} input
   * @returns {string}
   */
  #optionNameFrom(input) {
    if (input instanceof HTMLSelectElement) {
      const selectedOption = input.selectedOptions.item(0);
      if (selectedOption?.dataset.optionName) return selectedOption.dataset.optionName;
    }

    if (input.dataset.optionName) return input.dataset.optionName;

    const name = input.getAttribute('name') || '';
    const wrapped = name.match(/^options\[(.+)\]$/);
    return wrapped?.[1] || name;
  }

  /**
   * @param {HTMLElement} notice
   * @param {Record<string, string>} selected
   * @returns {boolean}
   */
  #noticeMatches(notice, selected) {
    const optionName = (notice.dataset.optionName || '').trim();
    const selectedValue = optionName ? selected[optionName] : undefined;
    const values = optionName ? (selectedValue ? [selectedValue] : []) : Object.values(selected);
    return this.#triggersMatch(notice, values);
  }

  /**
   * @param {HTMLElement} notice
   * @param {string[]} values
   * @returns {boolean}
   */
  #valuesMatchNotice(notice, values) {
    const triggers = this.#noticeTriggers(notice);
    if (!triggers.length) return false;
    const normalizedValues = values.flatMap((value) => this.#optionTokens(value));
    return normalizedValues.some((value) => triggers.includes(value));
  }

  /**
   * @param {HTMLElement} notice
   * @returns {string[]}
   */
  #noticeTriggers(notice) {
    return (notice.dataset.triggerValues || '')
      .split('||')
      .map((value) => this.#normalizeOptionValue(value))
      .filter(Boolean);
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  #normalizeOptionValue(value) {
    return this.#decodeEntities(value)
      .trim()
      .toLowerCase()
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ');
  }

  /**
   * @param {string} value
   * @returns {string[]}
   */
  #optionTokens(value) {
    const decoded = this.#decodeEntities(String(value || ''));
    const parts = decoded
      .split(/\s*\/\s*/)
      .flatMap((part) => part.split(/\s*[|,]\s*/))
      .map((part) => this.#normalizeOptionValue(part))
      .filter(Boolean);
    const whole = this.#normalizeOptionValue(decoded);
    if (whole && !parts.includes(whole)) parts.push(whole);
    return parts;
  }

  /**
   * @param {HTMLElement} notice
   * @param {boolean} enabled
   * @param {boolean} formAssociated
   */
  #setFieldsEnabled(notice, enabled, formAssociated) {
    const formId = this.dataset.productFormId || '';
    const fields = notice.querySelectorAll('input, select, textarea');
    for (const field of fields) {
      if (
        !(field instanceof HTMLInputElement) &&
        !(field instanceof HTMLSelectElement) &&
        !(field instanceof HTMLTextAreaElement)
      ) {
        continue;
      }
      field.disabled = !enabled;
      if (enabled && formAssociated && formId) {
        field.setAttribute('form', formId);
      } else if (enabled && !formAssociated) {
        field.removeAttribute('form');
      }
    }
  }

  /**
   * @param {HTMLElement | null} notice
   */
  #syncOtherField(notice) {
    if (!notice) return;
    const select = notice.querySelector('[data-variant-notice-condition]');
    const otherField = notice.querySelector('[data-variant-notice-other]');
    if (!(select instanceof HTMLSelectElement) || !(otherField instanceof HTMLElement)) return;

    const selectedKey = select.value.trim().toLowerCase().replace(/\s+/g, '_');
    const showOther = Boolean(selectedKey) && (selectedKey === 'other' || selectedKey.startsWith('other_'));
    otherField.hidden = !showOther;

    const input = otherField.querySelector('input, textarea');
    if (input instanceof HTMLInputElement || input instanceof HTMLTextAreaElement) {
      input.disabled = notice.hidden || !showOther;
      input.required = showOther && !notice.hidden;
      if (!showOther) input.value = '';
    }
  }

  /**
   * @param {string} value
   * @returns {string}
   */
  #decodeEntities(value) {
    return value
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&quot;/gi, '"')
      .replace(/&#39;/gi, "'");
  }

  #observeBundleBuilder() {
    /** @type {number | undefined} */
    let timer;
    this.#mutationObserver = new MutationObserver((mutations) => {
      const relevant = mutations.some((mutation) => {
        if (mutation.target instanceof Element && mutation.target.closest('variant-notice-component')) return false;
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (node.matches(BUNDLE_WIDGET_SELECTOR)) return true;
          if (node.querySelector(`${BUNDLE_WIDGET_SELECTOR}, .add-to-cart-button`)) return true;
        }
        return mutation.target instanceof Element && Boolean(mutation.target.closest(BUNDLE_WIDGET_SELECTOR));
      });
      if (!relevant) return;
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        patchGbbAddMethods();
        this.update();
      }, 50);
    });
    this.#mutationObserver.observe(document.body, { childList: true, subtree: true });
  }

  #syncPurchaseControls() {
    const nativeBlocking = this.#hasThemeInvalidNotice();
    const form = this.#productForm();
    const section = this.closest('.shopify-section');

    const nativeButtons = [
      ...(form ? form.querySelectorAll('.add-to-cart-button') : []),
      ...(section ? section.querySelectorAll('sticky-add-to-cart .add-to-cart-button') : []),
    ];

    for (const button of nativeButtons) {
      if (!(button instanceof HTMLElement)) continue;
      this.#setNoticeBlocked(button, nativeBlocking, true);
    }

    for (const button of document.querySelectorAll(GBB_ATC_SELECTOR)) {
      if (!(button instanceof HTMLElement)) continue;
      this.#setNoticeBlocked(button, false, false);
    }

    const accelerated = form?.querySelector('.accelerated-checkout-block');
    if (!(accelerated instanceof HTMLElement)) return;

    if (nativeBlocking) {
      accelerated.hidden = true;
      accelerated.dataset.noticeBlocked = 'true';
      return;
    }

    if (accelerated.dataset.noticeBlocked === 'true') {
      delete accelerated.dataset.noticeBlocked;
      accelerated.hidden = false;
    }
  }

  /**
   * @param {HTMLElement} button
   * @param {boolean} blocking
   * @param {boolean} nativeDisable
   */
  #setNoticeBlocked(button, blocking, nativeDisable) {
    const isNativeButton = button instanceof HTMLButtonElement || button instanceof HTMLInputElement;

    if (blocking) {
      const alreadyBlocked = button.dataset.noticeBlocked === 'true';
      if (!alreadyBlocked && (!isNativeButton || !button.disabled)) {
        button.dataset.noticeBlocked = 'true';
      }
      if (nativeDisable && isNativeButton) button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
      return;
    }

    if (button.dataset.noticeBlocked === 'true') {
      delete button.dataset.noticeBlocked;
      if (nativeDisable && isNativeButton) button.disabled = false;
      button.removeAttribute('aria-disabled');
    }
  }

  /**
   * True when a confirmation-required variant is selected (theme or bundle widget) and incomplete.
   * @returns {boolean}
   */
  blocksBundleAdd() {
    for (const variantId of this.#bundleSelectedVariantIds()) {
      const notice = this.#noticeForVariant(variantId);
      if (notice && !this.#noticeIsValid(notice)) return true;
    }

    return false;
  }

  /**
   * @param {unknown} payload
   * @param {{gbbRequest?: boolean}} [options]
   * @returns {boolean}
   */
  shouldBlockCartAdd(payload, options = {}) {
    this.#harvestBundleVariantsFromPayload(payload);
    this.update();
    if (this.#isGlobal() && this.#productComponentCovers(payload, options)) return false;
    const bundleRequest = Boolean(options.gbbRequest);
    const requiredId = this.#requiredNoticeVariantId(payload, { gbbRequest: bundleRequest });
    if (requiredId) this.#pendingRequiredVariantId = requiredId;

    if (requiredId) {
      const notice = this.#noticeForVariant(requiredId);
      if (notice && !this.#noticeIsValid(notice)) return true;
    }

    return bundleRequest ? this.blocksBundleAdd() : this.#hasThemeInvalidNotice();
  }

  /**
   * True when this component has a notice that applies to the payload / bundle selection.
   * @param {unknown} [payload]
   * @param {{gbbRequest?: boolean}} [options]
   * @returns {boolean}
   */
  coversPayload(payload, options = {}) {
    this.#harvestBundleVariantsFromPayload(payload);
    this.#harvestBundleVariants();
    /** @type {Set<string>} */
    const ids = new Set(extractVariantIds(payload));
    if (options.gbbRequest) {
      for (const id of this.#bundleSelectedVariantIds()) ids.add(id);
    }
    for (const id of ids) {
      if (this.#noticeForVariant(id)) return true;
    }
    return false;
  }

  /**
   * @param {unknown} [payload]
   * @param {{gbbRequest?: boolean}} [options]
   * @returns {boolean}
   */
  #productComponentCovers(payload, options = {}) {
    for (const element of document.querySelectorAll('variant-notice-component')) {
      if (element === this || !(element instanceof VariantNoticeComponent)) continue;
      if (element.dataset.scope === 'global') continue;
      if (element.coversPayload(payload, options)) return true;
    }
    return false;
  }

  /**
   * @param {unknown} payload
   * @param {{gbbRequest?: boolean}} [options]
   * @returns {Record<string, Record<string, string>>}
   */
  propertiesByVariantId(payload, options = {}) {
    /** @type {Record<string, Record<string, string>>} */
    const map = {};
    const payloadIds = extractVariantIds(payload);
    const sourceIds = options.gbbRequest
      ? [...payloadIds, ...this.#bundleSelectedVariantIds()]
      : payloadIds;

    /** @type {Record<string, string> | null} */
    let fallbackProperties = null;

    for (const variantId of sourceIds) {
      const notice = this.#noticeForVariant(variantId);
      if (!notice || !this.#noticeIsValid(notice)) continue;
      const properties = this.#propertiesFromNotice(notice);
      if (!Object.keys(properties).length) continue;
      if (payloadIds.includes(variantId)) map[variantId] = properties;
      fallbackProperties = properties;
    }

    if (!Object.keys(map).length && fallbackProperties && options.gbbRequest) {
      for (const variantId of payloadIds) map[variantId] = fallbackProperties;
    }

    return map;
  }

  shouldPromptOnBlock() {
    return true;
  }

  showBundleNotice() {
    this.#preferModal = true;
    this.reportNoticeValidity();
  }

  reportNoticeValidity() {
    const variantId = this.#requiredNoticeVariantId(undefined, { gbbRequest: this.#preferModal });
    if (variantId) this.#pendingRequiredVariantId = variantId;

    if (this.#preferModal) {
      this.#preferModal = false;
      if (variantId) this.#openNoticeModal(variantId);
      return;
    }

    this.#focusNotice(variantId);
  }

  /**
   * @param {unknown} [payload]
   * @param {{gbbRequest?: boolean}} [options]
   * @returns {string}
   */
  #requiredNoticeVariantId(payload, options = {}) {
    /** @type {string[]} */
    const candidates = [];

    candidates.push(...extractVariantIds(payload));
    if (options.gbbRequest) {
      candidates.push(...this.#bundleSelectedVariantIds());
    } else {
      const current = this.#currentVariantId();
      if (current) candidates.push(current);
    }

    for (const id of candidates) {
      const variantId = normalizeShopifyId(id);
      const notice = variantId ? this.#noticeForVariant(variantId) : null;
      if (notice && !this.#noticeIsValid(notice)) return variantId;
    }

    return '';
  }

  /**
   * @returns {string}
   */
  #currentVariantId() {
    const input = this.#productForm()?.querySelector('input[name="id"]');
    if (input instanceof HTMLInputElement && input.value) return normalizeShopifyId(input.value);

    const section = this.closest('.shopify-section');
    const checked = section?.querySelector('variant-picker input[data-variant-id]:checked');
    if (checked instanceof HTMLInputElement) return normalizeShopifyId(checked.dataset.variantId || '');

    const selected = section?.querySelector('variant-picker option[data-variant-id][selected], variant-picker select option:checked');
    if (selected instanceof HTMLOptionElement) return normalizeShopifyId(selected.dataset.variantId || '');

    return '';
  }

  /**
   * @returns {boolean}
   */
  #isModalOpen() {
    return Boolean(this.#modal?.open);
  }

  /**
   * @param {string} variantId
   */
  #openNoticeModal(variantId) {
    const notice = this.#noticeForVariant(variantId);
    const modal = this.#modal;
    const body = this.#modalBody;
    if (!(notice instanceof HTMLElement) || !(modal instanceof HTMLDialogElement) || !(body instanceof HTMLElement)) {
      this.#focusNotice(variantId);
      return;
    }

    if (modal.open && this.#modalNotice === notice) return;

    if (this.#modalNotice && this.#modalNotice !== notice) {
      this.#returnModalNotice();
    }

    this.#modalNotice = notice;
    notice.hidden = false;
    this.#setFieldsEnabled(notice, true, this.#noticeMatches(notice, this.#selectedOptions()));
    this.#syncOtherField(notice);
    body.appendChild(notice);

    const title = notice.querySelector('.variant-notice__title');
    if (title?.id) modal.setAttribute('aria-labelledby', title.id);
    else modal.removeAttribute('aria-labelledby');

    if (modal.parentElement !== document.body) {
      document.body.appendChild(modal);
    }

    if (!modal.open) modal.showModal();

    window.requestAnimationFrame(() => {
      this.#firstInvalidField(notice)?.focus({ preventScroll: true });
    });
  }

  /**
   * @param {{restore?: boolean}} [options]
   */
  #closeNoticeModal(options = {}) {
    if (this.#closingModal) return;
    this.#closingModal = true;
    const modal = this.#modal;
    if (modal instanceof HTMLDialogElement && modal.open) {
      modal.close();
    } else {
      this.#returnModalNotice();
      if (options.restore !== false) this.update();
    }
    this.#closingModal = false;
  }

  #returnModalNotice() {
    const notice = this.#modalNotice;
    const list = this.refs.noticeList;
    if (notice instanceof HTMLElement && list instanceof HTMLElement && notice.parentElement !== list) {
      list.appendChild(notice);
    }
    this.#modalNotice = null;
  }

  #onModalCloseClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.#modal?.close();
  };

  #onModalClose = () => {
    this.#returnModalNotice();
    this.#preferModal = false;
    this.update();
  };

  #onModalConfirm = () => {
    const notice = this.#modalNotice;
    if (notice && !this.#noticeIsValid(notice)) {
      const field = this.#firstInvalidField(notice);
      field?.reportValidity();
      return;
    }

    const retryButton = this.#retryBundleButton;
    this.#userInitiatedAdd = true;
    this.#modal?.close();
    if (retryButton instanceof HTMLElement) {
      window.setTimeout(() => {
        if (!this.blocksBundleAdd()) retryButton.click();
        this.#userInitiatedAdd = false;
      }, 0);
    } else {
      this.#userInitiatedAdd = false;
    }
  };

  /**
   * @param {MouseEvent} event
   */
  #onModalBackdropClick = (event) => {
    if (event.target === this.#modal) this.#modal.close();
  };

  /**
   * @param {string} [variantId]
   */
  #focusNotice(variantId) {
    this.update();

    const notice = (variantId && this.#noticeForVariant(variantId)) ||
      this.#noticeElements().find((element) => !element.hidden && !this.#noticeIsValid(element)) ||
      null;

    if (notice) {
      for (const other of this.#noticeElements()) {
        if (other === notice) continue;
        if (other.dataset.cartRequired === 'true' && !this.#noticeMatches(other, this.#selectedOptions())) {
          other.hidden = true;
          delete other.dataset.cartRequired;
          this.#setFieldsEnabled(other, false, false);
        }
      }

      notice.hidden = false;
      this.hidden = false;
      this.toggleAttribute('data-active', true);
      const formAssociated =
        !variantId ||
        this.#currentVariantId() === variantId ||
        this.#noticeMatches(notice, this.#selectedOptions());
      this.#setFieldsEnabled(notice, true, formAssociated);
      this.#syncOtherField(notice);
    }

    const field = notice ? this.#firstInvalidField(notice) : null;
    const target = field || notice;
    target?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    if (field) {
      field.focus({ preventScroll: true });
      field.reportValidity();
      return;
    }
    this.#productForm()?.reportValidity();
  }

  /**
   * @returns {boolean}
   */
  #hasThemeInvalidNotice() {
    const selected = this.#selectedOptions();
    for (const notice of this.#noticeElements()) {
      if (!this.#noticeMatches(notice, selected)) continue;
      if (!this.#noticeIsValid(notice)) return true;
    }
    return false;
  }

  /**
   * @returns {boolean}
   */
  #hasInvalidNotice() {
    for (const notice of this.#noticeElements()) {
      if (notice.hidden) continue;
      if (!this.#noticeIsValid(notice)) return true;
    }
    return false;
  }

  /**
   * @param {HTMLElement} notice
   * @returns {boolean}
   */
  #noticeIsValid(notice) {
    const fields = notice.querySelectorAll('input, select, textarea');
    for (const field of fields) {
      if (
        !(field instanceof HTMLInputElement) &&
        !(field instanceof HTMLSelectElement) &&
        !(field instanceof HTMLTextAreaElement)
      ) {
        continue;
      }
      if (field.disabled) continue;
      if (!field.checkValidity()) return false;
    }
    return true;
  }

  /**
   * @param {HTMLElement} notice
   * @returns {HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null}
   */
  #firstInvalidField(notice) {
    const fields = notice.querySelectorAll('input, select, textarea');
    for (const field of fields) {
      if (
        !(field instanceof HTMLInputElement) &&
        !(field instanceof HTMLSelectElement) &&
        !(field instanceof HTMLTextAreaElement)
      ) {
        continue;
      }
      if (field.disabled) continue;
      if (!field.checkValidity()) return field;
    }
    return null;
  }

  /**
   * @param {string | number} variantId
   * @returns {HTMLElement | null}
   */
  #noticeForVariant(variantId) {
    const matches = this.#noticeElements().filter((notice) => this.#variantRequiresNotice(variantId, notice));
    if (!matches.length) return null;
    const assigned = matches.find((notice) => notice.dataset.productAssigned !== 'false');
    if (assigned) return assigned;
    const preferredHandles = new Set(['vat-free', 'vat-free-uk', 'vat-incontinent', 'vat-free-incontinent']);
    return matches.find((notice) => preferredHandles.has(notice.dataset.handle || '')) || matches[0];
  }

  /**
   * @param {string | number} variantId
   * @param {HTMLElement} notice
   * @returns {boolean}
   */
  #variantRequiresNotice(variantId, notice) {
    const variant = this.#variantById(variantId);
    if (variant) return this.#variantMatchesNotice(notice, variant, { bundle: notice.dataset.productAssigned === 'false' });

    const values = this.#valuesForVariantId(variantId);
    if (!values.length) return false;
    if (notice.dataset.productAssigned === 'false' && !this.#noticeTriggers(notice).length) return false;
    return this.#triggersMatch(notice, values);
  }

  /**
   * @param {HTMLElement} notice
   * @param {{id: string, options: Record<string, string>}} variant
   * @returns {boolean}
   */
  #variantMatchesNotice(notice, variant, options = {}) {
    const optionName = (notice.dataset.optionName || '').trim();
    const named = optionName ? variant.options?.[optionName] : '';
    /** @type {string[]} */
    const allValues = [
      ...Object.values(variant.options || {}),
      ...(variant.values || []),
      variant.title || '',
    ].filter(Boolean);
    const values = named ? [named] : allValues;
    if (optionName && !named) {
      if (options.bundle && !this.#noticeTriggers(notice).length) return false;
      return this.#triggersMatch(notice, allValues);
    }
    if (options.bundle && !this.#noticeTriggers(notice).length) return false;
    return this.#triggersMatch(notice, values.filter(Boolean));
  }

  /**
   * Option values and trigger quantity each work on their own.
   * When both are set, both must match.
   * @param {HTMLElement} notice
   * @param {string[]} values
   * @returns {boolean}
   */
  #triggersMatch(notice, values) {
    const hasOptionTrigger = this.#noticeTriggers(notice).length > 0;
    const hasQuantityTrigger = this.#noticeTriggerQuantity(notice) > 0;
    if (!hasOptionTrigger && !hasQuantityTrigger) return false;

    const optionOk = !hasOptionTrigger || this.#valuesMatchNotice(notice, values);
    return optionOk && this.#quantityMeetsTrigger(notice);
  }

  /**
   * Quantity selector value for this product section. Defaults to 1 when none is present.
   * @returns {number}
   */
  #selectedQuantity() {
    const form = this.#productForm();
    const section = this.closest('.shopify-section');
    const roots = [form, section];

    for (const root of roots) {
      if (!(root instanceof HTMLElement)) continue;
      const input = root.querySelector('quantity-selector-component input[name="quantity"]');
      if (!(input instanceof HTMLInputElement)) continue;
      const value = Number.parseInt(input.value, 10);
      if (Number.isFinite(value)) return value;
    }

    return 1;
  }

  /**
   * @param {HTMLElement} notice
   * @returns {number}
   */
  #noticeTriggerQuantity(notice) {
    const triggerQuantity = Number.parseInt(notice.dataset.triggerQuantity || '0', 10);
    return Number.isFinite(triggerQuantity) ? triggerQuantity : 0;
  }

  /**
   * When Trigger quantity is set and not 0, the selected quantity must be at or above it.
   * @param {HTMLElement} notice
   * @returns {boolean}
   */
  #quantityMeetsTrigger(notice) {
    const triggerQuantity = this.#noticeTriggerQuantity(notice);
    if (triggerQuantity <= 0) return true;
    return this.#selectedQuantity() >= triggerQuantity;
  }

  /**
   * @param {HTMLElement} notice
   * @returns {Record<string, string>}
   */
  #propertiesFromNotice(notice) {
    /** @type {Record<string, string>} */
    const properties = {};
    const fields = notice.querySelectorAll('input, select, textarea');
    for (const field of fields) {
      if (
        !(field instanceof HTMLInputElement) &&
        !(field instanceof HTMLSelectElement) &&
        !(field instanceof HTMLTextAreaElement)
      ) {
        continue;
      }
      if (field.disabled || !field.name) continue;
      if (field instanceof HTMLInputElement && field.type === 'checkbox' && !field.checked) continue;
      const matched = field.name.match(/^properties\[(.+)\]$/);
      if (!matched?.[1] || !field.value) continue;
      properties[matched[1]] = field.value;
    }
    return properties;
  }

  /**
   * Variant IDs currently selected in bundle widgets on this page.
   * @returns {string[]}
   */
  #bundleSelectedVariantIds() {
    /** @type {Set<string>} */
    const ids = new Set();
    for (const entry of this.#bundleVariantEntries()) {
      if (entry.id) ids.add(entry.id);
    }
    return [...ids];
  }

  /**
   * @returns {boolean}
   */
  #hasBundleWidget() {
    return Boolean(document.querySelector(BUNDLE_WIDGET_SELECTOR));
  }

  /**
   * @returns {{id: string, values: string[], quantity: number}[]}
   */
  #bundleVariantEntries() {
    /** @type {Map<string, {id: string, values: string[], quantity: number}>} */
    const entries = new Map();

    const remember = (id, values = [], quantity = 1) => {
      const variantId = normalizeShopifyId(id);
      if (!variantId) return;
      const current = entries.get(variantId) || { id: variantId, values: [], quantity: 1 };
      for (const value of values) {
        if (value && !current.values.includes(value)) current.values.push(value);
      }
      if (Number.isFinite(quantity) && quantity > 0) current.quantity = quantity;
      entries.set(variantId, current);
    };

    const nodes = document.querySelectorAll(
      [
        '.gbbMixProductItem[data-variant-id]',
        '.gbbMixProductCard[data-variant-id]',
        '.gbbMixVariantSelect',
        '.gbbMixDropdownMobile',
        '.gbbMixProductCard select',
        '.gbbMixVariantOptions:checked',
        '[class*="gbbMix"] [data-variant-id]',
        '[class*="gbbMix"] select',
        '[class*="gbbExt"] [data-variant-id]',
        '[class*="gbbExt"] select',
        '[class*="gfb"] [data-variant-id]',
        '[class*="gfb"] select',
        '[id*="gfbBundle"] [data-variant-id]',
        '[id*="fly_bundles"] [data-variant-id]',
      ].join(',')
    );

    for (const node of nodes) {
      if (!(node instanceof HTMLElement)) continue;
      let variantId = '';
      /** @type {string[]} */
      const values = [];
      if (node instanceof HTMLSelectElement) {
        const selected = node.selectedOptions.item(0);
        variantId = selected?.getAttribute('data-variant-id') || node.value;
        if (selected?.textContent) values.push(selected.textContent);
        if (selected?.getAttribute('data-option-value')) values.push(selected.getAttribute('data-option-value') || '');
      } else if (node instanceof HTMLOptionElement) {
        variantId = node.getAttribute('data-variant-id') || node.value;
        if (node.textContent) values.push(node.textContent);
      } else if (node instanceof HTMLInputElement) {
        if ((node.type === 'checkbox' || node.type === 'radio') && !node.checked) continue;
        variantId = node.getAttribute('data-variant-id') || node.value;
        const label = node.closest('label')?.textContent || node.getAttribute('aria-label') || node.value;
        if (label) values.push(label);
      } else {
        variantId = node.getAttribute('data-variant-id') || '';
        const selectedLabel =
          node.querySelector('.gbbMixDropdownSelectedOptionContainer, .gbbMixCustomSelectSelectedOptionContainer')
            ?.textContent || '';
        if (selectedLabel) values.push(selectedLabel);
        if (node.getAttribute('data-variant-title')) values.push(node.getAttribute('data-variant-title') || '');
        if (node.getAttribute('data-option-value')) values.push(node.getAttribute('data-option-value') || '');
      }

      remember(variantId, values);
    }

    for (const item of this.#gbbSessionItems()) {
      const variantId = item.variant_id || item.id || item.variantId || '';
      remember(variantId, valuesFromUnknown(item), Number(item.quantity) || 1);
    }

    return [...entries.values()];
  }

  /**
   * @param {string | number} variantId
   * @returns {string[]}
   */
  #valuesForVariantId(variantId) {
    const id = normalizeShopifyId(variantId);
    const variant = this.#variantById(id);
    /** @type {string[]} */
    const values = [];
    if (variant) {
      values.push(...Object.values(variant.options || {}));
      if (Array.isArray(variant.values)) values.push(...variant.values);
      if (variant.title) values.push(variant.title);
    }
    const entry = this.#bundleVariantEntries().find((item) => item.id === id);
    if (entry) values.push(...entry.values);
    return values.filter(Boolean);
  }

  #harvestBundleVariants() {
    for (const entry of this.#bundleVariantEntries()) {
      mergeRemoteVariant(entry.id, { id: entry.id, options: {}, values: entry.values, title: entry.values[0] || '' });
    }
  }

  /**
   * @param {unknown} payload
   */
  #harvestBundleVariantsFromPayload(payload) {
    if (!payload || typeof payload !== 'object') return;
    const record = /** @type {Record<string, unknown>} */ (payload);
    const groups = [record.items, record.bundleItems].filter(Array.isArray);
    const items = groups.length ? groups.flat() : [record];
    for (const item of items) {
      if (!item || typeof item !== 'object') continue;
      const entry = /** @type {Record<string, unknown>} */ (item);
      const variantId = normalizeShopifyId(entry.id ?? entry.variant_id ?? entry.variantId ?? '');
      if (!variantId) continue;
      mergeRemoteVariant(variantId, {
        id: variantId,
        options: {},
        values: valuesFromUnknown(entry),
        title: String(entry.variant_title || entry.variantTitle || entry.title || ''),
      });
    }
  }

  #syncRemoteVariants() {
    if (this.#syncingRemote) return;
    const ids = this.#bundleSelectedVariantIds();
    if (!ids.length) return;
    this.#syncingRemote = true;
    ensureRemoteVariants(ids)
      .then((updated) => {
        this.#syncingRemote = false;
        if (updated) this.update();
      })
      .catch(() => {
        this.#syncingRemote = false;
      });
  }

  /**
   * @returns {Record<string, unknown>[]}
   */
  #gbbSessionItems() {
    try {
      const gbb = /** @type {any} */ (window).gbbMix;
      const data =
        gbb?.sdk?.f?.getCartData?.() ||
        gbb?.gbbMixAndMatchBundle?.state?.cartData ||
        gbb?.gbbMixBrowser?.getCartDataFromSessionStorage?.();
      if (Array.isArray(data?.items)) return data.items;
    } catch {
      // GBB may not be initialised yet.
    }

    try {
      const gfb = /** @type {any} */ (window).gfb;
      const flyData = gfb?.utility?.getCartData?.() || gfb?.gfbFrequentBought?.state?.cartData;
      if (Array.isArray(flyData?.items)) return flyData.items;
    } catch {
      // Fly Bundles may not be initialised yet.
    }

    try {
      for (let index = 0; index < sessionStorage.length; index++) {
        const key = sessionStorage.key(index);
        if (!key || !key.includes('gbbMix')) continue;
        const parsed = JSON.parse(sessionStorage.getItem(key) || 'null');
        if (Array.isArray(parsed?.items)) return parsed.items;
      }
    } catch {
      // Ignore unreadable session entries.
    }

    return [];
  }

  /**
   * @param {string | number} variantId
   * @returns {{id: string, options: Record<string, string>} | null}
   */
  #variantById(variantId) {
    const id = normalizeVariantId(variantId);
    const local = this.#variantMap().find((variant) => variant.id === id);
    if (local) return local;
    return remoteVariantMap.get(id) || null;
  }

  /**
   * @returns {{id: string, options: Record<string, string>}[]}
   */
  #variantMap() {
    const script = this.querySelector('script[data-variant-notice-variants]');
    if (!(script instanceof HTMLScriptElement)) return [];
    try {
      const parsed = JSON.parse(script.textContent || '[]');
      if (!Array.isArray(parsed)) return [];
      return parsed.map((entry) => ({
        id: normalizeVariantId(entry?.id),
        options: entry?.options && typeof entry.options === 'object' ? entry.options : {},
      }));
    } catch {
      return [];
    }
  }

  /**
   * @returns {HTMLFormElement | null}
   */
  #productForm() {
    const formId = this.dataset.productFormId;
    if (!formId) return null;
    const form = document.getElementById(formId);
    return form instanceof HTMLFormElement ? form : null;
  }
}

function installCartAddGuard() {
  if (cartAddGuardInstalled) return;
  cartAddGuardInstalled = true;

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (input, init) => {
    const url = requestUrl(input);
    const method = requestMethod(input, init);
    if (!isCartAddRequest(method, url)) return originalFetch(input, init);

    const rawBody = await readRequestBody(input, init);
    const parsed = parseCartAddBody(rawBody);
    await ensureRemoteVariants(extractVariantIds(parsed.payload));
    const gbbRequest = isBundleCartAdd(url, parsed.payload);
    const blocker = findBlockingNotice(parsed.payload, { gbbRequest });
    if (blocker) {
      blocker.showBundleNotice();
      return Promise.reject(new DOMException('Variant notice confirmation required', 'AbortError'));
    }

    const decorated = decorateCartAdd(parsed, { gbbRequest });
    if (input instanceof Request) {
      return originalFetch(url, {
        method: 'POST',
        headers: new Headers(init?.headers || input.headers),
        body: decorated,
        credentials: init?.credentials || input.credentials,
        signal: init?.signal || input.signal,
      });
    }

    return originalFetch(input, { ...init, body: decorated });
  };

  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;

  XMLHttpRequest.prototype.open = function (method, url, ...rest) {
    this.__variantNoticeMethod = method;
    this.__variantNoticeUrl = url;
    return originalOpen.call(this, method, url, ...rest);
  };

  XMLHttpRequest.prototype.send = function (body) {
    if (isCartAddRequest(this.__variantNoticeMethod, this.__variantNoticeUrl)) {
      const parsed = parseCartAddBody(body);
      const gbbRequest = isBundleCartAdd(this.__variantNoticeUrl, parsed.payload);
      ensureRemoteVariants(extractVariantIds(parsed.payload));
      const blocker = findBlockingNotice(parsed.payload, { gbbRequest });
      if (blocker) {
        blocker.showBundleNotice();
        this.abort();
        return;
      }
      body = decorateCartAdd(parsed, { gbbRequest });
    }
    return originalSend.call(this, body);
  };

  watchGbbPatch();
}

/**
 * @param {unknown} input
 * @returns {string}
 */
function requestUrl(input) {
  if (typeof input === 'string') return input;
  if (input instanceof Request) return input.url;
  if (input instanceof URL) return input.href;
  return String(input || '');
}

/**
 * @param {unknown} input
 * @param {RequestInit} [init]
 * @returns {string}
 */
function requestMethod(input, init) {
  if (init?.method) return init.method;
  if (input instanceof Request) return input.method;
  return 'GET';
}

/**
 * @param {unknown} input
 * @param {RequestInit} [init]
 * @returns {Promise<unknown>}
 */
async function readRequestBody(input, init) {
  if (init?.body != null) return init.body;
  if (input instanceof Request) return input.clone().text();
  return null;
}

/**
 * @param {string} method
 * @param {string} url
 * @returns {boolean}
 */
function isCartAddRequest(method, url) {
  if (String(method || 'GET').toUpperCase() !== 'POST') return false;
  try {
    const path = new URL(String(url), window.location.origin).pathname.replace(/\/+$/, '');
    return path === '/cart/add' || path === '/cart/add.js';
  } catch {
    return /\/cart\/add(?:\.js)?\/?$/.test(String(url));
  }
}

/**
 * @param {string} url
 * @returns {boolean}
 */
function isGbbCartAdd(url) {
  const value = String(url || '');
  return value.includes('gbbMixBundleApp') || value.includes('gbbMix') || value.includes('easy-bundle');
}

/**
 * @param {string} url
 * @returns {boolean}
 */
function isFlyCartAdd(url) {
  const value = String(url || '');
  return (
    value.includes('gfbBundle') ||
    value.includes('gfbBundleApp') ||
    value.includes('fly-bundles') ||
    value.includes('fly_bundles')
  );
}

/**
 * @param {string} url
 * @param {unknown} [payload]
 * @returns {boolean}
 */
function isBundleCartAdd(url, payload) {
  if (isGbbCartAdd(url) || isFlyCartAdd(url)) return true;
  if (payload && typeof payload === 'object') {
    const record = /** @type {Record<string, unknown>} */ (payload);
    if (record._flyBundles || record.gfbBundle || record.gbbMix) return true;
    const ids = extractVariantIds(payload);
    if (ids.length > 1) return true;
  }
  return false;
}

/**
 * @param {unknown} payload
 * @param {{gbbRequest?: boolean}} [options]
 * @returns {VariantNoticeComponent | null}
 */
function findBlockingNotice(payload, options = {}) {
  const components = [...document.querySelectorAll('variant-notice-component')].filter(
    (element) => element instanceof VariantNoticeComponent
  );
  const productComponents = components.filter((element) => element.dataset.scope !== 'global');
  const globalComponents = components.filter((element) => element.dataset.scope === 'global');

  for (const element of productComponents) {
    if (element.shouldBlockCartAdd(payload, options)) return element;
  }
  for (const element of globalComponents) {
    if (element.shouldBlockCartAdd(payload, options)) return element;
  }
  return null;
}

/**
 * @param {{raw: unknown, payload: unknown, kind: string}} parsed
 * @param {{gbbRequest?: boolean}} [options]
 * @returns {unknown}
 */
function decorateCartAdd(parsed, options = {}) {
  /** @type {Record<string, Record<string, string>>} */
  const propertiesById = {};
  const components = [...document.querySelectorAll('variant-notice-component')].filter(
    (element) => element instanceof VariantNoticeComponent
  );
  const ordered = [
    ...components.filter((element) => element.dataset.scope === 'global'),
    ...components.filter((element) => element.dataset.scope !== 'global'),
  ];
  for (const element of ordered) {
    Object.assign(propertiesById, element.propertiesByVariantId(parsed.payload, options));
  }
  if (!Object.keys(propertiesById).length) return parsed.raw;
  return applyProperties(parsed, propertiesById);
}

function patchGbbAddMethods() {
  /**
   * @param {Record<string, any>} target
   * @param {string} key
   */
  const wrap = (target, key) => {
    const original = target?.[key];
    if (typeof original !== 'function') return;
    target[key] = async function (...args) {
      const payload = args[0];
      await ensureRemoteVariants(extractVariantIds(payload));
      const blocker = findBlockingNotice(payload, { gbbRequest: true });
      if (blocker) {
        blocker.showBundleNotice();
        return false;
      }

      const looksLikeCart =
        payload &&
        typeof payload === 'object' &&
        !('target' in payload) &&
        ('items' in payload || 'bundleItems' in payload || 'id' in payload || 'variant_id' in payload);

      if (looksLikeCart) {
        const parsed = parseCartAddBody(JSON.stringify(payload));
        const decorated = decorateCartAdd(parsed, { gbbRequest: true });
        if (typeof decorated === 'string') {
          try {
            args[0] = JSON.parse(decorated);
          } catch {
            // Keep the original payload if decoration cannot be parsed.
          }
        }
      }

      return original.apply(this, args);
    };
  };

  const gbb = /** @type {any} */ (window).gbbMix;
  if (gbb?.utility && !gbb.utility.__variantNoticePatched) {
    gbb.utility.__variantNoticePatched = true;
    wrap(gbb.utility, 'addToCart');
    wrap(gbb.utility, 'addToCartV2');
    wrap(gbb.utility, 'executeAddToCart');
    wrap(gbb.utility, 'addBundleToCartBtnHandler');
    wrap(gbb.gbbMixAndMatchBundle?.f, 'addBundleToCart');
    wrap(gbb.sdk?.f, 'addBundleToCart');
    wrap(gbb.sdk?.utility, 'executeAddToCart');
  }

  const gfb = /** @type {any} */ (window).gfb;
  if (gfb?.utility && !gfb.utility.__variantNoticePatched) {
    gfb.utility.__variantNoticePatched = true;
    wrap(gfb.utility, 'addToCart');
    wrap(gfb.utility, 'addToCartV2');
    wrap(gfb.utility, 'executeAddToCart');
    wrap(gfb.gfbFrequentBought?.f, 'addToCart');
  }
}

function watchGbbPatch() {
  patchGbbAddMethods();
  const patched =
    /** @type {any} */ (window).gbbMix?.utility?.__variantNoticePatched ||
    /** @type {any} */ (window).gfb?.utility?.__variantNoticePatched;
  if (patched) return;

  const timer = window.setInterval(() => {
    patchGbbAddMethods();
    if (
      /** @type {any} */ (window).gbbMix?.utility?.__variantNoticePatched ||
      /** @type {any} */ (window).gfb?.utility?.__variantNoticePatched
    ) {
      window.clearInterval(timer);
    }
  }, 250);

  window.setTimeout(() => window.clearInterval(timer), 20000);
}

/**
 * @param {unknown} body
 * @returns {{raw: unknown, payload: unknown, kind: string}}
 */
function parseCartAddBody(body) {
  if (body == null || body === '') return { raw: body, payload: null, kind: 'empty' };

  if (body instanceof FormData) {
    return { raw: body, payload: formDataToPayload(body), kind: 'formdata' };
  }

  if (body instanceof URLSearchParams) {
    return { raw: body, payload: urlParamsToPayload(body), kind: 'params' };
  }

  if (typeof body === 'string') {
    try {
      return { raw: body, payload: JSON.parse(body), kind: 'json' };
    } catch {
      return { raw: body, payload: urlParamsToPayload(new URLSearchParams(body)), kind: 'params' };
    }
  }

  if (typeof body === 'object') return { raw: body, payload: body, kind: 'object' };
  return { raw: body, payload: null, kind: 'unknown' };
}

/**
 * @param {FormData} formData
 * @returns {Record<string, unknown>}
 */
function formDataToPayload(formData) {
  /** @type {Record<string, unknown>} */
  const payload = {};
  const id = formData.get('id') || formData.get('variant_id');
  if (id != null) payload.id = id;

  /** @type {Record<string, unknown>[]} */
  const items = [];
  for (const [key, value] of formData.entries()) {
    const matched = String(key).match(/^items\[(\d+)\]\[(id|variant_id)\]$/);
    if (!matched) continue;
    const index = Number(matched[1]);
    items[index] = items[index] || {};
    items[index][matched[2]] = value;
  }
  if (items.length) payload.items = items.filter(Boolean);

  return payload;
}

/**
 * @param {URLSearchParams} params
 * @returns {Record<string, unknown>}
 */
function urlParamsToPayload(params) {
  /** @type {Record<string, unknown>} */
  const payload = {};
  const id = params.get('id') || params.get('variant_id');
  if (id != null) payload.id = id;
  return payload;
}

/**
 * @param {unknown} payload
 * @returns {string[]}
 */
function extractVariantIds(payload) {
  if (!payload || typeof payload !== 'object') return [];

  /** @type {string[]} */
  const ids = [];
  const record = /** @type {Record<string, unknown>} */ (payload);
  const groups = [record.items, record.bundleItems].filter(Array.isArray);

  if (groups.length) {
    for (const group of groups) {
      for (const item of group) {
        if (!item || typeof item !== 'object') continue;
        const entry = /** @type {Record<string, unknown>} */ (item);
        const id = entry.id ?? entry.variant_id ?? entry.variantId;
        if (id != null) ids.push(normalizeShopifyId(id));
      }
    }
  }

  if (record.id != null) ids.push(normalizeShopifyId(record.id));
  if (record.variant_id != null) ids.push(normalizeShopifyId(record.variant_id));
  if (record.variantId != null) ids.push(normalizeShopifyId(record.variantId));
  return [...new Set(ids.filter(Boolean))];
}

/**
 * @param {unknown} id
 * @returns {string}
 */
function normalizeVariantId(id) {
  return normalizeShopifyId(id);
}

/**
 * @param {unknown} id
 * @returns {string}
 */
function normalizeShopifyId(id) {
  const value = String(id ?? '').trim();
  if (!value) return '';
  const gid = value.match(/\/(?:Product|ProductVariant)\/(\d+)/);
  if (gid?.[1]) return gid[1];
  if (/^\d+$/.test(value)) return value;
  const digits = value.match(/(\d{8,})/);
  return digits?.[1] || value;
}

/**
 * @param {{raw: unknown, payload: unknown, kind: string}} parsed
 * @param {Record<string, Record<string, string>>} propertiesById
 * @returns {unknown}
 */
function applyProperties(parsed, propertiesById) {
  const payload = parsed.payload;
  if (!payload || typeof payload !== 'object') return parsed.raw;

  const record = /** @type {Record<string, unknown>} */ (payload);

  const mergeItem = (/** @type {Record<string, unknown>} */ item) => {
    const id = normalizeVariantId(item.id ?? item.variant_id ?? '');
    const properties = propertiesById[id];
    if (!properties) return item;
    const current = item.properties && typeof item.properties === 'object' ? item.properties : {};
    item.properties = { ...current, ...properties };
    return item;
  };

  if (Array.isArray(record.items)) {
    record.items = record.items.map((item) =>
      item && typeof item === 'object' ? mergeItem(/** @type {Record<string, unknown>} */ (item)) : item
    );
  } else if (!Array.isArray(record.bundleItems)) {
    mergeItem(record);
  }

  if (Array.isArray(record.bundleItems)) {
    record.bundleItems = record.bundleItems.map((item) =>
      item && typeof item === 'object' ? mergeItem(/** @type {Record<string, unknown>} */ (item)) : item
    );
  }

  if (parsed.kind === 'json' || parsed.kind === 'object') return JSON.stringify(record);

  if (parsed.kind === 'params') {
    const params = new URLSearchParams(String(parsed.raw || ''));
    const id = normalizeVariantId(record.id ?? record.variant_id ?? '');
    const properties = propertiesById[id];
    for (const [key, value] of Object.entries(properties || {})) {
      params.set(`properties[${key}]`, value);
    }
    return params.toString();
  }

  if (parsed.kind === 'formdata' && parsed.raw instanceof FormData) {
    const id = normalizeVariantId(record.id ?? record.variant_id ?? '');
    const properties = propertiesById[id];
    for (const [key, value] of Object.entries(properties || {})) {
      parsed.raw.set(`properties[${key}]`, value);
    }
    return parsed.raw;
  }

  return parsed.raw;
}

/**
 * @param {unknown} item
 * @returns {string[]}
 */
function valuesFromUnknown(item) {
  if (!item || typeof item !== 'object') return [];
  const record = /** @type {Record<string, unknown>} */ (item);
  /** @type {string[]} */
  const values = [];
  const keys = [
    'variant_title',
    'variantTitle',
    'public_title',
    'publicTitle',
    'title',
    'option1',
    'option2',
    'option3',
    'name',
  ];
  for (const key of keys) {
    if (record[key] != null && record[key] !== '') values.push(String(record[key]));
  }
  if (record.options && typeof record.options === 'object') {
    if (Array.isArray(record.options)) {
      for (const value of record.options) {
        if (value) values.push(String(value));
      }
    } else {
      for (const value of Object.values(record.options)) {
        if (value) values.push(String(value));
      }
    }
  }
  return values;
}

/**
 * @param {string} id
 * @param {{id: string, options: Record<string, string>, values: string[], title: string}} record
 */
function mergeRemoteVariant(id, record) {
  const current = remoteVariantMap.get(id);
  if (!current) {
    remoteVariantMap.set(id, {
      id,
      options: { ...record.options },
      values: [...(record.values || [])].filter(Boolean),
      title: record.title || '',
    });
    return;
  }

  /** @type {string[]} */
  const values = [...current.values];
  for (const value of record.values || []) {
    if (value && !values.includes(value)) values.push(value);
  }

  remoteVariantMap.set(id, {
    id,
    options: { ...current.options, ...record.options },
    values,
    title: current.title || record.title || '',
  });
}

/**
 * @param {unknown} data
 * @returns {{id: string, options: Record<string, string>, values: string[], title: string} | null}
 */
function variantRecordFromAjax(data) {
  if (!data || typeof data !== 'object') return null;
  const record = /** @type {Record<string, unknown>} */ (data);
  const id = normalizeShopifyId(record.id);
  if (!id) return null;

  /** @type {Record<string, string>} */
  const options = {};
  /** @type {string[]} */
  const values = [];

  for (const key of ['option1', 'option2', 'option3']) {
    const value = record[key];
    if (value) {
      options[key] = String(value);
      values.push(String(value));
    }
  }

  if (Array.isArray(record.options)) {
    record.options.forEach((value, index) => {
      if (!value) return;
      const key = `option${index + 1}`;
      if (!options[key]) options[key] = String(value);
      if (!values.includes(String(value))) values.push(String(value));
    });
  }

  for (const key of ['title', 'public_title', 'name']) {
    if (record[key]) values.push(String(record[key]));
  }

  return {
    id,
    options,
    values,
    title: String(record.title || record.public_title || ''),
  };
}

/**
 * @param {string} id
 * @returns {Promise<{id: string, options: Record<string, string>, values: string[], title: string} | null>}
 */
function fetchVariantDetails(id) {
  const existing = pendingVariantFetches.get(id);
  if (existing) return existing;

  const request = fetch(`/variants/${id}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  })
    .then((response) => {
      if (!response.ok) throw new Error(`Failed to fetch variant ${id}`);
      return response.json();
    })
    .then((data) => {
      const record = variantRecordFromAjax(data);
      if (record) mergeRemoteVariant(id, record);
      return record;
    })
    .catch(() => null)
    .finally(() => {
      fetchedVariantIds.add(id);
      pendingVariantFetches.delete(id);
    });

  pendingVariantFetches.set(id, request);
  return request;
}

/**
 * @param {string[]} ids
 * @returns {Promise<boolean>}
 */
async function ensureRemoteVariants(ids) {
  const unique = [...new Set((ids || []).map((id) => normalizeShopifyId(id)).filter(Boolean))];
  if (!unique.length) return false;

  const tasks = unique.map((id) => {
    if (fetchedVariantIds.has(id)) return Promise.resolve(null);
    return fetchVariantDetails(id);
  });

  if (!tasks.length) return false;
  const results = await Promise.all(tasks);
  return results.some(Boolean);
}

if (!customElements.get('variant-notice-component')) {
  customElements.define('variant-notice-component', VariantNoticeComponent);
}
